import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type {
  Attendance,
  ContactInput,
  OccurrenceKey,
  StudentDetail,
  UpdateStudentProfileRequest,
} from "@edu/shared";
import * as api from "../../lib/api.ts";

/**
 * Карточка ученика: загрузка и обвязка действий.
 *
 * Отдельным хуком, потому что читателей у карточки теперь двое: страница
 * (`/students/:id`) и окно поверх списка. Одни и те же данные, одни и те же
 * действия, и разъехавшись, они дали бы окно, которое перечитывает карточку
 * после перевыпуска ссылки, и страницу, которая забывает это сделать.
 *
 * ⚠️ Один запрос на всё: пара, судьба ссылки, занятия и доля верных ответов
 * приезжают вместе (`GET /v1/students/:id`). Карточку открывают переходом, и три
 * круга по сети на канале 128 кбит/с (раздел 3.1 CLAUDE.md) человек видит как
 * подвисание.
 */
export interface StudentCardState {
  data: StudentDetail | null;
  err: string | null;
  busy: boolean;
  /**
   * Код приглашения, показанный в ЭТОЙ сессии экрана.
   *
   * ⚠️ Живёт только в памяти и теряется при перезагрузке, и это не недоделка: в
   * базе лежит только SHA-256, и показать ссылку второй раз сервер не может.
   */
  link: string | null;
  reload: () => Promise<void>;
  run: (fn: () => Promise<void>) => void;
  reissue: () => void;
  toggleArchive: () => void;
  revoke: () => void;
  /* ── анкета и учёт ─────────────────────────────────────────────────── */
  saveProfile: (patch: UpdateStudentProfileRequest) => void;
  addContact: (input: ContactInput) => void;
  editContact: (contactId: string, input: ContactInput) => void;
  removeContact: (contactId: string) => void;
  /**
   * Отметить занятие: оплату, посещение или и то и другое.
   *
   * Через тот же `run`, что и остальное, то есть с перечитыванием карточки.
   * Оптимистичного обновления здесь нет намеренно: отметка меняет не одну
   * галочку, а счётчики долга и пропусков, а считает их сервер по раскрытию
   * расписания. Угадать новые числа на клиенте значит показать те, что
   * разойдутся с приехавшими следом.
   */
  setMark: (key: OccurrenceKey, patch: { paid?: boolean; attendance?: Attendance | null }) => void;
  /** Подвести черту: отметить оплаченными все занятия до локальной даты. */
  settle: (before: string) => void;
  /** Отметить месяц оплаченным или снять отметку. Помесячная пара. */
  setMonth: (period: string, paid: boolean) => void;
}

export function useStudent(studentId: string): StudentCardState {
  const [data, setData] = useState<StudentDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    try {
      setData(await api.getStudent(studentId));
      setErr(null);
    } catch (e) {
      /**
       * Ученик в адресе перенесён в другой аккаунт (8.3
       * `docs/account-adopt-design.md`), а смотрящий учит выжившего: карточка
       * переходит сама. Заменой адреса, без записи в историю, иначе «назад»
       * снова вело бы в надгробие и обратно сюда.
       */
      const movedTo =
        e instanceof api.ApiError && e.code === "student_moved" ? e.details.movedTo : undefined;
      if (typeof movedTo === "string" && movedTo !== studentId) {
        void navigate(`/students/${movedTo}`, { replace: true });
        return;
      }
      // Чужой и несуществующий ученик одинаково 404: сервер отвечает так
      // намеренно, и объяснять здесь разницу нечем
      setErr(
        e instanceof api.ApiError && e.status === 404
          ? "Ученик не найден"
          : e instanceof Error
            ? e.message
            : String(e),
      );
    }
  }, [studentId, navigate]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const run = useCallback(
    (fn: () => Promise<void>) => {
      void (async () => {
        setBusy(true);
        setErr(null);
        try {
          await fn();
          await reload();
        } catch (e) {
          setErr(e instanceof Error ? e.message : String(e));
        } finally {
          setBusy(false);
        }
      })();
    },
    [reload],
  );

  const reissue = useCallback(() => {
    if (data === null) return;
    const userId = data.student.userId;
    run(async () => {
      setLink((await api.reissueInvite(userId)).inviteUrl);
    });
  }, [data, run]);

  const toggleArchive = useCallback(() => {
    if (data === null) return;
    const { userId, archiveVisible } = data.student;
    run(async () => {
      await api.setStudentArchiveVisible(userId, !archiveVisible);
    });
  }, [data, run]);

  const revoke = useCallback(() => {
    if (data === null) return;
    const userId = data.student.userId;
    run(async () => {
      // Видимость архива не трогаем: у неё свой переключатель, и решать за
      // преподавателя два вопроса одной кнопкой мы больше не будем
      await api.unlinkStudent(userId, false);
      setLink(null);
    });
  }, [data, run]);

  /**
   * Идентификатор ученика для действий анкеты.
   *
   * Берётся из `studentId` (адрес), а не из `data.student.userId`, и это не
   * придирка: действия анкеты вызываются из форм, которые живут в поддереве
   * загруженной карточки, но `data` в замыкании обновляется позже, чем
   * пользователь успевает нажать вторую кнопку. Адрес же не меняется.
   */
  const forStudent = useCallback(
    (fn: (id: string) => Promise<void>) => {
      if (studentId === "") return;
      run(() => fn(studentId));
    },
    [run, studentId],
  );

  const saveProfile = useCallback(
    // Пустую строку заметки отправляем как есть: приводит её к «нет заметки»
    // сервер, в одном месте, иначе два приведения разъедутся
    (patch: UpdateStudentProfileRequest) => forStudent((id) => api.updateStudentProfile(id, patch)),
    [forStudent],
  );

  const addContact = useCallback(
    (input: ContactInput) =>
      forStudent(async (id) => {
        await api.addStudentContact(id, input);
      }),
    [forStudent],
  );

  const editContact = useCallback(
    (contactId: string, input: ContactInput) =>
      forStudent(async (id) => {
        await api.updateStudentContact(id, contactId, input);
      }),
    [forStudent],
  );

  const removeContact = useCallback(
    (contactId: string) => forStudent((id) => api.deleteStudentContact(id, contactId)),
    [forStudent],
  );

  const setMark = useCallback(
    (key: OccurrenceKey, patch: { paid?: boolean; attendance?: Attendance | null }) =>
      forStudent((id) => api.setStudentMark(id, key, patch)),
    [forStudent],
  );

  const settle = useCallback(
    (before: string) =>
      forStudent(async (id) => {
        await api.settleStudentPayments(id, before);
      }),
    [forStudent],
  );

  const setMonth = useCallback(
    (period: string, paid: boolean) => forStudent((id) => api.setStudentMonth(id, period, paid)),
    [forStudent],
  );

  return {
    data,
    err,
    busy,
    link,
    reload,
    run,
    reissue,
    toggleArchive,
    revoke,
    saveProfile,
    addContact,
    editContact,
    removeContact,
    setMark,
    settle,
    setMonth,
  };
}
